import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import {
  JournalEntryCard,
  JournalEntryDocChip,
  JournalEntryExternalLink,
  JournalEntryPostedBadge,
} from '@cottage/design-system/internal-tooling/ui'
import JournalEntryPreview from '@/components/JournalEntryPreview'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import WarningBanner from '@/components/WarningBanner'
import { usePageAccess } from '@/hooks/usePageAccess'
import { useQBOData } from '@/hooks/useQBOData'
import {
  deleteWaterfallJournalEntryFromQbo,
  generateWaterfallJournalEntry,
  getWaterfallJournalEntryStatus,
  pushWaterfallJournalEntryQboUpdate,
} from '@/services/waterfall'
import {
  getQboIdMappings,
  postJournalEntry,
  updateJournalEntryLines,
  type JournalEntryLine,
  type QboIdMappingItem,
} from '@/services/accounting'
import type { WaterfallJeMode, WaterfallJournalEntryStatus } from '@/types/waterfall'
import { formatApiError } from '@/utils/apiError'
import { toast } from '@/store/toastStore'
import {
  formatJePostedDate,
  reportMonthLabel,
} from './waterfallJeStatus'
import { parseJeGenerateError, parseJeReadinessBlockedError, type AllocationReadinessChange } from './allocationMonthReadinessUtils'
import AllocationMonthReadiness from './AllocationMonthReadiness'

type Props = {
  mode: WaterfallJeMode
  month?: string
  monthOptions?: string[]
  monthSelectOptions?: SelectOption[]
  onMonthChange?: (month: string) => void
  canPostJe?: boolean
  showReadinessChecklist?: boolean
  onReadinessChange?: (state: AllocationReadinessChange) => void
  distributionNum?: number
  distributionLabel?: string
  onDismiss?: () => void
  onChanged?: () => void
}

function isLinesBalanced(lines: JournalEntryLine[]): boolean {
  const debits = lines
    .filter((line) => line.posting_type === 'Debit')
    .reduce((sum, line) => sum + Number(line.amount), 0)
  const credits = lines
    .filter((line) => line.posting_type === 'Credit')
    .reduce((sum, line) => sum + Number(line.amount), 0)
  return Math.abs(debits - credits) < 0.005
}

function defaultGaDepartment(
  mappings: QboIdMappingItem[],
  locations: { id: string; name: string }[],
): Pick<JournalEntryLine, 'department_id' | 'department_name'> {
  const mapping = mappings.find((item) => item.key === 'department.g_and_a')
  if (mapping) {
    return { department_id: mapping.qbo_id, department_name: mapping.display_name }
  }
  const location = locations.find((item) => item.name === 'G&A')
  return location
    ? { department_id: location.id, department_name: location.name }
    : { department_id: null, department_name: null }
}

export default function WaterfallJournalEntryCard({
  mode,
  month,
  monthOptions,
  monthSelectOptions,
  onMonthChange,
  canPostJe = true,
  showReadinessChecklist = false,
  onReadinessChange,
  distributionNum,
  distributionLabel,
  onDismiss,
  onChanged,
}: Props) {
  const { canEdit } = usePageAccess('distributions')
  const { locations } = useQBOData()
  const isCashDistribution = mode === 'cash' && distributionNum != null
  const [status, setStatus] = useState<WaterfallJournalEntryStatus | null>(null)
  const [draftLines, setDraftLines] = useState<JournalEntryLine[]>([])
  const [actionLoading, setActionLoading] = useState(false)
  const [posting, setPosting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [missingAccountKeys, setMissingAccountKeys] = useState<string[]>([])
  const [postedMenuOpen, setPostedMenuOpen] = useState(false)
  const [accountOptions, setAccountOptions] = useState<SelectOption[]>([])
  const [qboMappings, setQboMappings] = useState<QboIdMappingItem[]>([])
  const linesSaveGenerationRef = useRef(0)

  const locationOptions: SelectOption[] = useMemo(
    () => locations.map((location) => ({ id: location.id, label: location.name })),
    [locations]
  )
  const newLineDefaults = useMemo(
    () => defaultGaDepartment(qboMappings, locations),
    [qboMappings, locations]
  )
  const missingLocations = useMemo(
    () => draftLines.some((line) => !line.department_id && !line.department_name?.trim()),
    [draftLines]
  )

  const draft = status?.draft ?? null
  const balanced = useMemo(() => isLinesBalanced(draftLines), [draftLines])
  const incomeCreatePostBlocked = mode === 'income' && !canPostJe
  const isDraft = draft?.status === 'draft'
  const isPosted = draft?.status === 'posted'
  const showDirty =
    Boolean(status?.content_dirty) || Boolean(status?.needs_qbo_update) || status?.status === 'stale'
  const hideQboUpdate = isCashDistribution
  const blockedPostedCashRegenerate = hideQboUpdate && isPosted

  const statusParams = useMemo(() => {
    if (isCashDistribution) {
      return { distribution_num: distributionNum }
    }
    if (month) {
      return { month }
    }
    return null
  }, [isCashDistribution, distributionNum, month])

  const refreshStatus = useCallback(async () => {
    if (!statusParams) return
    setError(null)
    try {
      const next = await getWaterfallJournalEntryStatus(mode, statusParams)
      setStatus(next)
      setDraftLines((next.draft?.lines as JournalEntryLine[]) ?? [])
    } catch (e) {
      setError(formatApiError(e, 'Failed to load journal entry status'))
      setStatus(null)
      setDraftLines([])
    }
  }, [mode, statusParams])

  useEffect(() => {
    void refreshStatus()
  }, [refreshStatus])

  useEffect(() => {
    async function loadQboMappings() {
      try {
        const response = await getQboIdMappings()
        setQboMappings(response.mappings)
        setAccountOptions(
          response.mappings
            .filter((mapping) => mapping.mapping_type === 'account')
            .map((mapping) => ({ id: mapping.qbo_id, label: mapping.display_name }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      } catch (err) {
        console.error('Failed to load QBO account mappings:', err)
      }
    }
    void loadQboMappings()
  }, [])

  const notifyChanged = useCallback(async () => {
    await refreshStatus()
    onChanged?.()
  }, [refreshStatus, onChanged])

  const handleLinesChange = useCallback(
    async (next: JournalEntryLine[]) => {
      setDraftLines(next)
      if (!draft?.id) return
      const saveGeneration = ++linesSaveGenerationRef.current
      try {
        const updated = await updateJournalEntryLines(draft.id, next)
        if (saveGeneration !== linesSaveGenerationRef.current) return
        setDraftLines(updated.lines)
        setStatus((prev) =>
          prev?.draft
            ? {
                ...prev,
                draft: {
                  ...prev.draft,
                  lines: updated.lines,
                  total_debits: updated.total_debits,
                  total_credits: updated.total_credits,
                },
              }
            : prev
        )
      } catch (e) {
        if (saveGeneration !== linesSaveGenerationRef.current) return
        toast.error(formatApiError(e, 'Failed to save line changes'))
      }
    },
    [draft?.id]
  )

  const canEditDraftLines = canEdit && isDraft && !posting

  const handleCreate = async (overwritePosted = false) => {
    if (!canEdit || !statusParams) return
    setActionLoading(true)
    setError(null)
    setMissingAccountKeys([])
    try {
      const result = await generateWaterfallJournalEntry({
        mode,
        ...(isCashDistribution
          ? { distribution_num: distributionNum }
          : { month }),
        overwrite_posted: overwritePosted,
      })
      setStatus({
        mode,
        report_month: month ?? result.draft.txn_date.slice(0, 7),
        status: result.needs_qbo_update ? 'stale' : 'draft',
        doc_number: result.draft.doc_number,
        distribution_num: isCashDistribution ? distributionNum : undefined,
        content_dirty: result.needs_qbo_update,
        needs_qbo_update: result.needs_qbo_update,
        draft: result.draft,
      })
      setDraftLines(result.draft.lines as JournalEntryLine[])
      toast.success(
        overwritePosted ? 'Journal entry draft overwritten' : 'Journal entry draft created',
      )
      await notifyChanged()
    } catch (e) {
      const parsed = parseJeGenerateError(e)
      setError(parsed.message)
      setMissingAccountKeys(parsed.missingKeys)
      if (parsed.readinessBlocked) {
        toast.error(parsed.readinessBlocked.message)
      }
    } finally {
      setActionLoading(false)
    }
  }

  const handleRegenerate = async () => {
    if (!canEdit || !draft) return
    if (isPosted && !hideQboUpdate) {
      const confirmed = window.confirm(
        draft.doc_number
          ? `Overwrite ${draft.doc_number} from the current ledger?`
          : 'Overwrite this journal entry from the current ledger?',
      )
      if (!confirmed) return
      await handleCreate(true)
      return
    }
    if (isPosted && hideQboUpdate) {
      toast.error('Posted cash journal entries cannot be overwritten from this page.')
      return
    }
    await handleCreate(false)
  }

  const handlePost = async () => {
    if (!canEdit || !draft || !balanced || missingLocations) return
    setPosting(true)
    try {
      await updateJournalEntryLines(draft.id, draftLines)
      await postJournalEntry(draft.id)
      toast.success('Journal entry posted to QuickBooks')
      await notifyChanged()
    } catch (e) {
      const readinessBlocked = parseJeReadinessBlockedError(e)
      if (readinessBlocked) {
        toast.error(readinessBlocked.message)
        setError(readinessBlocked.message)
      } else {
        toast.error(formatApiError(e, 'Failed to post journal entry'))
      }
    } finally {
      setPosting(false)
    }
  }

  const handleDeleteQbo = async () => {
    if (!canEdit || !draft || hideQboUpdate) return
    const allocationMonth =
      month ?? (draft.source_data?.report_month as string | undefined)
    const allocationMonthLabel = allocationMonth
      ? reportMonthLabel(allocationMonth)
      : 'this month'
    const baseConfirm = draft.doc_number
      ? `Delete ${draft.doc_number} from QuickBooks?`
      : 'Delete this journal entry from QuickBooks?'
    const confirmed = window.confirm(
      `${baseConfirm} This will also remove the logged allocation for ${allocationMonthLabel} from history.`,
    )
    if (!confirmed) return

    setActionLoading(true)
    setPostedMenuOpen(false)
    try {
      const result = await deleteWaterfallJournalEntryFromQbo(draft.id)
      setDraftLines(result.draft.lines as JournalEntryLine[])
      toast.success(
        'Journal entry deleted from QuickBooks and allocation history reversed',
      )
      await notifyChanged()
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to delete journal entry from QuickBooks'))
    } finally {
      setActionLoading(false)
    }
  }

  const handlePushQboUpdate = async () => {
    if (!canEdit || !draft || hideQboUpdate) return
    const confirmed = window.confirm(
      draft.doc_number
        ? `Update ${draft.doc_number} in QuickBooks from the current ledger?`
        : 'Update this journal entry in QuickBooks from the current ledger?',
    )
    if (!confirmed) return

    setActionLoading(true)
    setPostedMenuOpen(false)
    try {
      const result = await pushWaterfallJournalEntryQboUpdate(draft.id, {
        force_regenerate: true,
      })
      setDraftLines(result.draft.lines as JournalEntryLine[])
      toast.success(
        result.regenerated
          ? 'Regenerated draft and updated QuickBooks'
          : 'Updated journal entry in QuickBooks',
      )
      await notifyChanged()
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update QuickBooks'))
    } finally {
      setActionLoading(false)
    }
  }

  const txnDateLabel = formatJePostedDate(draft?.txn_date ?? null)
  const hasMonthSelect =
    !isCashDistribution && monthSelectOptions && monthSelectOptions.length > 0 && onMonthChange
  const hasLegacyMonthSelect =
    !isCashDistribution && !hasMonthSelect && monthOptions && monthOptions.length > 0 && onMonthChange
  const showRegenerate =
    canEdit && Boolean(draft) && !isPosted && !blockedPostedCashRegenerate
  const cashContextLabel =
    distributionLabel ?? (distributionNum != null ? `Distribution #${distributionNum}` : null)
  const cardTitle =
    isCashDistribution && cashContextLabel ? cashContextLabel : 'Journal entry'

  const monthSelectLeading =
    hasMonthSelect ? (
      <SearchableSelect
        id={`waterfall-je-month-${mode}`}
        options={monthSelectOptions}
        value={month || null}
        onChange={(id) => id && onMonthChange(id)}
        disabled={!monthSelectOptions?.length}
        preserveOrder
        preferBelow
        useViewportForPlacement
        placeholder="Select month"
        className="min-w-[9rem] text-xs"
      />
    ) : hasLegacyMonthSelect ? (
      <select
        value={month}
        onChange={(e) => onMonthChange(e.target.value)}
        className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
        aria-label="Journal entry month"
      >
        {monthOptions.map((option) => (
          <option key={option} value={option}>
            {reportMonthLabel(option)}
          </option>
        ))}
      </select>
    ) : null

  const hasHeaderAlerts =
    Boolean(error) ||
    missingAccountKeys.length > 0 ||
    (draft?.warnings ?? []).length > 0 ||
    Boolean(draft?.error_message)
  const hasBodyAlerts =
    Boolean(draft && draftLines.length > 0 && isDraft && !balanced) ||
    Boolean(draft && draftLines.length > 0 && isDraft && missingLocations) ||
    Boolean(draft && showDirty)

  return (
    <JournalEntryCard
      posted={isPosted}
      title={cardTitle}
      leading={monthSelectLeading}
      identity={
        <>
          {draft?.doc_number ? (
            <JournalEntryDocChip>{draft.doc_number}</JournalEntryDocChip>
          ) : null}
          {txnDateLabel ? <span>{txnDateLabel}</span> : null}
          {isPosted && draft?.qbo_url ? (
            <JournalEntryExternalLink href={draft.qbo_url}>
              View in QuickBooks
            </JournalEntryExternalLink>
          ) : null}
        </>
      }
      actions={
        <>
          {!isPosted && canEdit && status?.status === 'none' && !isCashDistribution ? (
            <button
              type="button"
              onClick={() => void handleCreate(false)}
              disabled={actionLoading || !month || incomeCreatePostBlocked}
              className="btn btn-primary text-xs"
            >
              {actionLoading ? 'Creating…' : 'Create JE draft'}
            </button>
          ) : null}

          {showRegenerate ? (
            <button
              type="button"
              onClick={() => void handleRegenerate()}
              disabled={actionLoading || posting}
              className="btn btn-primary text-xs"
            >
              {actionLoading ? 'Regenerating…' : 'Regenerate'}
            </button>
          ) : null}

          {canEdit && isDraft && draft ? (
            <button
              type="button"
              onClick={() => void handlePost()}
              disabled={posting || !balanced || missingLocations || incomeCreatePostBlocked}
              className="btn btn-primary text-xs"
            >
              {posting ? 'Posting…' : 'Post to QuickBooks'}
            </button>
          ) : null}

          {isPosted ? (
            hideQboUpdate ? (
              <JournalEntryPostedBadge loading={actionLoading} />
            ) : (
              <JournalEntryPostedBadge
                loading={actionLoading}
                menuOpen={postedMenuOpen}
                onMenuOpenChange={setPostedMenuOpen}
                menu={
                  <>
                    {canEdit ? (
                      <button
                        type="button"
                        role="menuitem"
                        className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
                        disabled={actionLoading}
                        onClick={() => {
                          setPostedMenuOpen(false)
                          void handleRegenerate()
                        }}
                      >
                        Regenerate
                      </button>
                    ) : null}
                    <button
                      type="button"
                      role="menuitem"
                      className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
                      disabled={actionLoading}
                      onClick={() => void handlePushQboUpdate()}
                    >
                      Update Existing JE
                    </button>
                    {canEdit ? (
                      <button
                        type="button"
                        role="menuitem"
                        className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
                        disabled={actionLoading}
                        onClick={() => void handleDeleteQbo()}
                      >
                        Delete Existing JE
                      </button>
                    ) : null}
                  </>
                }
              />
            )
          ) : null}

          {onDismiss ? (
            <button
              type="button"
              onClick={onDismiss}
              className="btn border border-gray-200 bg-white px-2 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              aria-label="Close journal entry"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
        </>
      }
      alerts={
        hasHeaderAlerts || hasBodyAlerts
          ? (
              <div className="space-y-2">
                {error ? (
                  <p className="text-xs text-red-600" role="alert">
                    {error}
                  </p>
                ) : null}
                {missingAccountKeys.length > 0 ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                    <p className="font-medium">QuickBooks account IDs not configured:</p>
                    <ul className="mt-1 list-disc pl-4">
                      {missingAccountKeys.map((key) => (
                        <li key={key}>
                          <code>{key}</code>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {(draft?.warnings ?? []).map((warning, index) => (
                  <WarningBanner key={index} message={warning} />
                ))}
                {draft?.error_message ? <WarningBanner message={draft.error_message} /> : null}
                {draft && draftLines.length > 0 && isDraft && !balanced ? (
                  <WarningBanner message="Journal entry is out of balance. Adjust lines before posting." />
                ) : null}
                {draft && draftLines.length > 0 && isDraft && missingLocations ? (
                  <WarningBanner message="Assign a location on every line before posting." />
                ) : null}
                {showDirty ? (
                  <WarningBanner
                    message={
                      blockedPostedCashRegenerate
                        ? 'Ledger may have changed since this entry was posted. Open in QuickBooks to review or edit; this page cannot update posted cash journal entries.'
                        : hideQboUpdate
                          ? 'Ledger changed since this draft was saved. Regenerate before posting.'
                          : 'Ledger changed since this draft was saved. Regenerate before posting, or overwrite and update QuickBooks if already posted.'
                    }
                  />
                ) : null}
              </div>
            )
          : undefined
      }
    >
      {showReadinessChecklist && month ? (
        <AllocationMonthReadiness
          embedded
          month={month}
          onReadinessChange={onReadinessChange}
        />
      ) : null}

      {draft && draftLines.length > 0 ? (
        <div className="max-h-[28rem] overflow-hidden">
          <JournalEntryPreview
            lines={draftLines}
            variant="split"
            hideHeader
            scrollable
            className="flex max-h-[24rem] min-h-0 flex-col"
            totalDebits={draft.total_debits}
            totalCredits={draft.total_credits}
            accountOptions={accountOptions}
            editableAmounts={canEditDraftLines}
            editableLines={canEditDraftLines}
            editableLocations={canEditDraftLines}
            locationOptions={locationOptions}
            newLineDefaults={newLineDefaults}
            onLinesChange={canEditDraftLines ? (next) => void handleLinesChange(next) : undefined}
          />
        </div>
      ) : null}
    </JournalEntryCard>
  )
}
